import React, { useCallback, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';
import Cookies from 'js-cookie';

import accountsAPI from '@/api/accounts';
import { ErrorAlert } from '@/components/Alert';
import Button, { BUTTON_KIND, BUTTON_SIZE } from '@/components/Button';
import Input from '@/components/Input';
import InputCheckbox from '@/components/InputCheckbox';
import AgreementNotice from '@/components/LoginPage/AgreementNotice';
import { validateEmail } from '@/helpers/validate';
import useAuth from '@/hooks/useAuth';
import useAuthRedirect from '@/hooks/useAuthRedirect';
import {
  AUTH_OPTION_TYPES,
  GetAuthOptionsResponse,
  isOAuthProvider,
} from '@/types/IAuthOptions';

import LoadingAnimation from '../LoadingAnimation';
import Divider from './OrContinueWithDivider';
import SocialLoginButtons from './SocialLoginButtons';

interface LoginFormConfig {
  /** Whether this is for Shopify authentication flow */
  isShopify?: boolean;
  /** Custom redirect path after successful authentication */
  redirectPath?: string;
}

interface LoginFormProps {
  /** Configuration for the login form behavior */
  config?: LoginFormConfig;
}

const LoginForm: React.FC<LoginFormProps> = ({ config = {} }) => {
  const { isShopify = false, redirectPath = '/' } = config;

  const { login, shopifyLogin, loading, authError } = useAuth();
  const location = useLocation();

  // Use custom hook to handle authentication redirects
  const { isRedirecting } = useAuthRedirect({
    isShopify,
    redirectPath,
  });

  // Extract activation key for login calls
  const activationKey = location.state?.activationKey;

  const [email, setEmail] = useState(localStorage.getItem('mfa_email') || '');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // Email First flow
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [authOptions, setAuthOptions] = useState<GetAuthOptionsResponse | null>(
    null,
  );
  const [authOptionsLoading, setAuthOptionsLoading] = useState(false);
  const [authOptionsError, setAuthOptionsError] = useState<string | null>(null);

  // Memoized change handlers for better performance
  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
  }, []);

  const handlePasswordChange = useCallback((value: string) => {
    setPassword(value);
    // Clear password error when user starts typing again
    setPasswordMessage(prev => (prev ? '' : prev));
  }, []);

  const handleRememberMeChange = useCallback((value: boolean) => {
    setRememberMe(value);
  }, []);

  // Reset form state to initial values
  const resetFormState = useCallback(() => {
    setEmailSubmitted(false);
    setAuthOptions(null);
    setPassword('');
    setPasswordMessage('');
  }, []);

  // Memoized change handler for email reset
  const handleChangeEmail = useCallback(() => {
    resetFormState();
  }, [resetFormState]);

  const onContinueEmail = useCallback(() => {
    setEmailError(false);
    setAuthOptionsError(null);

    if (validateEmail(email)) {
      setAuthOptionsLoading(true);
      accountsAPI
        .getAuthOptions(email)
        .then(res => {
          setAuthOptions(res.data.data);
          setEmailSubmitted(true);
        })
        .catch(() => {
          setAuthOptionsError('Failed to fetch authentication options.');
        })
        .finally(() => setAuthOptionsLoading(false));
    } else {
      setEmailError(true);
    }
  }, [email]);

  // Validate password meets requirements
  const validatePassword = useCallback((password: string): boolean => {
    if (!password || password.trim() === '') {
      setPasswordMessage('Password is required');
      return false;
    }
    return true;
  }, []);

  const onLogin = useCallback(() => {
    setEmailError(false);
    setPasswordMessage('');

    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid) {
      setEmailError(true);
    }
    // Password errors are handled directly in the validatePassword function
    if (isEmailValid && isPasswordValid) {
      // Use appropriate login method based on configuration
      if (isShopify) {
        shopifyLogin({
          email,
          password,
          rememberMe,
        });
      } else {
        login({
          email,
          password,
          rememberMe,
          activationKeyFromLogin: activationKey,
        });
      }
    }
  }, [
    email,
    password,
    rememberMe,
    isShopify,
    activationKey,
    shopifyLogin,
    login,
    validatePassword,
  ]);

  const onSsoLogin = useCallback(() => {
    accountsAPI.getSsoAuthUrl(email).then(res => {
      if (typeof window !== 'undefined' && window.location) {
        Cookies.set('sso_csrf_token', res.data.csrf_token, {
          expires: 300,
        });
        window.location.href = res.data.url;
      }
    });
  }, [email]);

  // Memoized keyboard event handlers for performance
  const handleEmailKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') onContinueEmail();
    },
    [onContinueEmail],
  );

  const handlePasswordKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') onLogin();
    },
    [onLogin],
  );

  // Show loading state while redirecting - moved after all hooks
  if (isRedirecting) {
    return <LoadingAnimation className="w-16 h-16 mx-auto text-brand-500" />;
  }

  return (
    <div className="space-y-4">
      {emailError && <ErrorAlert message="Please enter a valid email" />}
      {passwordMessage && <ErrorAlert message={passwordMessage} />}
      {authOptionsError && <ErrorAlert message={authOptionsError} />}
      <AnimatePresence mode="wait" initial={false}>
        {!emailSubmitted ? (
          <motion.div
            key="email-form"
            initial={{ x: -40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -40, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="space-y-4"
          >
            <Input
              label="Email address"
              type="email"
              value={email}
              onChange={handleEmailChange}
              error={emailError}
              disabled={authOptionsLoading}
              onKeyDown={handleEmailKeyDown}
            />
            <div>
              <Button
                buttonText="Continue"
                onClick={onContinueEmail}
                fullWidth
                loading={authOptionsLoading}
              />
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="password-form"
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 80, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="space-y-4"
          >
            {authError && <ErrorAlert message="Invalid email or password" />}
            <div className="flex items-center justify-between">
              <span
                className="text-base font-medium text-gray-900"
                style={{ minWidth: 0, overflowWrap: 'anywhere' }}
                aria-label={`Current email: ${email}`}
                role="status"
                aria-live="polite"
              >
                {email}
              </span>
              <Button
                kind={BUTTON_KIND.COLOR_OMITTED_PURPLE}
                size={BUTTON_SIZE.XSMALL}
                buttonText="Change"
                onClick={handleChangeEmail}
                aria-label="Change email address"
              />
            </div>
            {/* Render login methods */}
            {authOptions && (
              <>
                <div className="flex flex-col gap-2">
                  {authOptions.login_methods.some(
                    m => m.type === AUTH_OPTION_TYPES.PASSWORD,
                  ) && (
                    <div className="space-y-4">
                      <Input
                        label="Password"
                        type="password"
                        value={password}
                        onChange={handlePasswordChange}
                        onKeyDown={handlePasswordKeyDown}
                        error={!!passwordMessage}
                      />
                      <div className="flex items-center justify-between text-xs">
                        <InputCheckbox
                          id="remember-me"
                          label="Remember me"
                          value={rememberMe}
                          onChange={handleRememberMeChange}
                          labelClassName="text-xs"
                        />
                        <Link to="/reset-password" className="text-xs">
                          Forgot your password?
                        </Link>
                      </div>
                      <Button
                        buttonText="Sign in"
                        onClick={onLogin}
                        fullWidth
                        loading={loading}
                      />
                    </div>
                  )}
                  {authOptions.login_methods.some(
                    m => m.type === AUTH_OPTION_TYPES.SAML,
                  ) && (
                    <Button
                      kind={BUTTON_KIND.WHITE}
                      buttonText="Sign in with Single Sign-On"
                      onClick={onSsoLogin}
                      fullWidth
                      disabled={loading}
                    />
                  )}
                </div>
                <AgreementNotice mode="signin" />
                {/* Only show social buttons if oauth methods are available */}
                {authOptions.login_methods.some(m =>
                  isOAuthProvider(m.type),
                ) && (
                  <>
                    {authOptions.login_methods.length > 2 && (
                      <Divider text="Or continue with" />
                    )}
                    <SocialLoginButtons isShopify={isShopify} />
                  </>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LoginForm;
